import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useOrganization } from '@/context/organization-context';
import { paths } from '@/routes/paths';
import { FEATURE_FLAGS, useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useEmailBuilderControllerCreateBlank } from '@/apis/backend/generated/email-builder/email-builder';
import { isQuotaExceededError } from '@/lib/services/quota-error';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/emails/new')({
  component: NewEmailBuilderPage,
});

function NewEmailBuilderPage() {
  const navigate = useNavigate();
  const { orgId, clientId } = Route.useParams();
  const { activeOrganization, activeClient } = useOrganization();
  const createBlank = useEmailBuilderControllerCreateBlank();
  // The flag map arrives with the org payload, so both effects wait on the org
  // rather than acting on the false `useFeatureFlag` returns before it lands.
  const selfServeEnabled = useFeatureFlag(FEATURE_FLAGS.SELF_SERVE_BUILDER);

  useEffect(() => {
    if (!activeOrganization?.id || !activeClient?.id || selfServeEnabled) return;
    navigate({ to: paths.dashboard.tenant(orgId, clientId).home, replace: true });
  }, [activeOrganization?.id, activeClient?.id, selfServeEnabled, navigate, orgId, clientId]);

  useEffect(() => {
    if (
      !activeOrganization?.id ||
      !activeClient?.id ||
      !selfServeEnabled ||
      createBlank.isPending ||
      createBlank.isSuccess
    ) {
      return;
    }
    createBlank.mutate(
      {
        orgId: activeOrganization.id,
        clientId: activeClient.id,
      },
      {
        onSuccess: template => {
          navigate({ to: paths.dashboard.tenant(orgId, clientId).emailEdit(template.id) });
        },
      },
    );
  }, [activeOrganization?.id, activeClient?.id, selfServeEnabled, createBlank, navigate]);

  if (!activeOrganization?.id || !activeClient?.id) {
    return <Alert severity="info">Select an organization and client to start a new email builder draft.</Alert>;
  }

  if (createBlank.isError) {
    // A quota refusal is a settled answer, not a failure to retry — say so.
    return isQuotaExceededError(createBlank.error) ? (
      <Alert severity="warning">
        You have used all your email generations for this period. Upgrade your
        plan to create more.
      </Alert>
    ) : (
      <Alert severity="error">Failed to create a new email builder draft.</Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
      <CircularProgress />
      <Typography variant="h6">Creating a blank email builder draft…</Typography>
    </Box>
  );
}
